<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { useIntersectionObserver } from '@vueuse/core';
import AppFooter from '@/components/AppFooter.vue';
import { t } from '~/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const missionRef = ref<HTMLElement | null>(null);
const missionVisible = ref(false);
useIntersectionObserver(
  missionRef,
  ([entry]) => {
    if (entry?.isIntersecting) missionVisible.value = true;
  },
  { threshold: 0.3 }
);

const cardRefs = ref<HTMLElement[]>([]);
const cardVisible = ref([false, false, false, false]);
onMounted(() => {
  cardRefs.value.forEach((el, i) => {
    useIntersectionObserver(
      ref(el),
      ([entry]) => {
        if (entry?.isIntersecting) cardVisible.value[i] = true;
      },
      { threshold: 0.15 }
    );
  });
});
const requestURL = useRequestURL();
const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/about'));
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

// SEO meta —— 用 computed 包成函数，让 useHead 能跟着 i18n locale 切换响应式更新。
// 注意：SSR 阶段 locale 永远 = 'en'，hydration 后客户端首屏切语言时 useHead 会复跑。
useHead(() => ({
  title: t('landingPages.about.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: t('landingPages.about.seo.description') },
    { name: 'keywords', content: t('landingPages.about.seo.keywords') },
    { property: 'og:title', content: t('landingPages.about.seo.ogTitle') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:description', content: t('landingPages.about.seo.description') },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: t('landingPages.about.seo.ogTitle') },
    { name: 'twitter:description', content: t('landingPages.about.seo.description') },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    ...buildPathHreflangLinks('/about'),
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    // 非阻塞加载 Google Fonts CSS（preload + onload swap）。
    // 普通 stylesheet 会阻塞首屏渲染，国内访问 fonts.googleapis.com 时会触发 FOUC。
    {
      rel: 'preload',
      as: 'style',
      href: 'https://fonts.googleapis.com/css2?family=Radley:ital@0;1&display=swap',
      onload: "this.onload=null;this.rel='stylesheet'",
    },
  ],
}));

/**
 * Hero 标题分行 + 分词。
 * - i18n key 中用 `\n` 控制换行；译者可自由调整每行词数（不强制 6 词）。
 * - 模板基于 (lineIdx, wordIdx) 计算 `--i` 变量驱动错峰 blur 入场动画。
 */
const heroLines = computed(() =>
  t('landingPages.about.hero.title')
    .split('\n')
    .map((line) => line.trim().split(/\s+/).filter(Boolean))
);
/** 把 (lineIdx, wordIdx) 映射成全局递增的动画延迟序号 */
const wordAnimIndex = (lineIdx: number, wordIdx: number) => {
  let count = 0;
  for (let i = 0; i < lineIdx; i++) count += heroLines.value[i]?.length || 0;
  return count + wordIdx;
};

/** Values 卡片数据（与 4 张卡片视觉的顺序对应；插入新项要同步 SVG 序号 0..3） */
const valueItems = computed(() => [
  {
    key: 'imagination',
    title: t('landingPages.about.values.items.imagination.title'),
    description: t('landingPages.about.values.items.imagination.description'),
  },
  {
    key: 'simplicity',
    title: t('landingPages.about.values.items.simplicity.title'),
    description: t('landingPages.about.values.items.simplicity.description'),
  },
  {
    key: 'personalization',
    title: t('landingPages.about.values.items.personalization.title'),
    description: t('landingPages.about.values.items.personalization.description'),
  },
  {
    key: 'designForEveryone',
    title: t('landingPages.about.values.items.designForEveryone.title'),
    description: t('landingPages.about.values.items.designForEveryone.description'),
  },
]);
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <AppNav active-path="/about" />

    <!-- Vision Hero Section -->
    <section class="relative h-[100vh] flex items-center justify-center overflow-hidden about-hero">
      <div class="relative container mx-auto px-6 md:px-4">
        <div class="max-w-[900px] mx-auto text-center">
          <!-- 分行 + 分词渲染：每词独立 word-blur span，--i 变量驱动 stagger 入场动画。
               i18n hero.title 通过 `\n` 控制换行（见 en.js 注释）。 -->
          <h1
            class="font-boldonse text-4xl md:text-6xl lg:text-7xl text-white uppercase tracking-tight leading-[128%] md:leading-[132%] lg:leading-[144%]"
          >
            <template v-for="(line, lineIdx) in heroLines" :key="lineIdx">
              <span
                v-for="(word, wordIdx) in line"
                :key="wordIdx"
                class="word-blur"
                :style="{ '--i': wordAnimIndex(lineIdx, wordIdx) }"
              >
                {{ word }}{{ wordIdx < line.length - 1 ? ' ' : '' }}
              </span>
              <br v-if="lineIdx < heroLines.length - 1" />
            </template>
          </h1>
        </div>
      </div>
    </section>

    <!-- Mission Statement Section -->
    <section class="py-48">
      <div class="container mx-auto">
        <div class="max-w-[800px] mx-auto px-8 md:px-4">
          <h2 class="sr-only">{{ $t('landingPages.about.mission.srHeading') }}</h2>
          <!-- mission body 含 <span> 高亮标记；用 v-html 渲染 i18n 文案，让译文能自由
               调整高亮短语位置。span 仅用于 text 颜色对比，无脚本风险。 -->
          <h3
            ref="missionRef"
            class="text-3xl md:text-5xl font-bold text-black/90 tracking-tight text-left anim-scroll"
            :class="{ 'is-visible': missionVisible }"
            v-html="$t('landingPages.about.mission.body')"
          ></h3>
        </div>
      </div>
    </section>

    <!-- Main About Section -->
    <section class="bg-white pb-32">
      <div class="container mx-auto px-2">
        <div class="max-w-[800px] mx-auto">
          <!-- Values 区块（保留 v-for 结构以兼容未来再加 mission/story 等 section） -->
          <div class="space-y-20">
            <div>
              <h2 class="text-xl font-bold p-6 text-black/90">
                {{ $t('landingPages.about.values.heading') }}
              </h2>

              <!-- Values 4-column grid，4 张卡片对应 SVG illustration 0..3 -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="(item, index) in valueItems"
                  :key="item.key"
                  :ref="
                    (el) => {
                      if (el) cardRefs[index] = el as HTMLElement;
                    }
                  "
                  class="aspect-square bg-[#fafafa] rounded-3xl p-6 flex flex-col anim-scroll"
                  :class="{ 'is-visible': cardVisible[index] }"
                  :style="{ transitionDelay: `${index * 0.1}s` }"
                >
                  <!-- Illustration area -->
                  <div class="flex-1 flex items-center justify-center">
                    <div class="w-[80%] aspect-square flex items-center justify-center">
                      <!-- Card 0: Blinking black circles -->
                      <svg v-if="index === 0" viewBox="0 0 200 200" class="w-full h-full">
                        <circle cx="20" cy="25" r="4" fill="#000" class="pop-1" />
                        <circle cx="175" cy="20" r="3" fill="#000" class="pop-2" />
                        <circle cx="100" cy="45" r="5" fill="#000" class="pop-3" />
                        <circle cx="35" cy="100" r="3" fill="#000" class="pop-4" />
                        <circle cx="170" cy="90" r="4" fill="#000" class="pop-5" />
                        <circle cx="80" cy="130" r="5" fill="#000" class="pop-6" />
                        <circle cx="20" cy="170" r="3" fill="#000" class="pop-7" />
                        <circle cx="140" cy="160" r="4" fill="#000" class="pop-8" />
                        <circle cx="180" cy="175" r="3" fill="#000" class="pop-9" />
                        <circle cx="60" cy="60" r="3" fill="#000" class="pop-10" />
                        <circle cx="130" cy="110" r="3" fill="#000" class="pop-11" />
                        <circle cx="50" cy="180" r="2" fill="#000" class="pop-12" />
                      </svg>

                      <!-- Card 1: Fractal Tree growing from bottom -->
                      <svg v-else-if="index === 1" viewBox="0 0 200 200" class="w-full h-full">
                        <!-- Trunk -->
                        <line
                          x1="100"
                          y1="200"
                          x2="100"
                          y2="150"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-trunk"
                        />
                        <!-- Level 1 -->
                        <line
                          x1="100"
                          y1="150"
                          x2="65"
                          y2="115"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l1"
                        />
                        <line
                          x1="100"
                          y1="150"
                          x2="135"
                          y2="115"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l1"
                        />
                        <!-- Level 2 -->
                        <line
                          x1="65"
                          y1="115"
                          x2="40"
                          y2="90"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l2"
                        />
                        <line
                          x1="65"
                          y1="115"
                          x2="80"
                          y2="85"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l2"
                        />
                        <line
                          x1="135"
                          y1="115"
                          x2="120"
                          y2="85"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l2"
                        />
                        <line
                          x1="135"
                          y1="115"
                          x2="160"
                          y2="90"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l2"
                        />
                        <!-- Level 3 -->
                        <line
                          x1="40"
                          y1="90"
                          x2="22"
                          y2="70"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="40"
                          y1="90"
                          x2="50"
                          y2="65"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="80"
                          y1="85"
                          x2="68"
                          y2="60"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="80"
                          y1="85"
                          x2="92"
                          y2="58"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="120"
                          y1="85"
                          x2="108"
                          y2="58"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="120"
                          y1="85"
                          x2="132"
                          y2="60"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="160"
                          y1="90"
                          x2="150"
                          y2="65"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <line
                          x1="160"
                          y1="90"
                          x2="178"
                          y2="70"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l3"
                        />
                        <!-- Level 4 -->
                        <line
                          x1="22"
                          y1="70"
                          x2="10"
                          y2="55"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="22"
                          y1="70"
                          x2="28"
                          y2="50"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="50"
                          y1="65"
                          x2="42"
                          y2="45"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="50"
                          y1="65"
                          x2="58"
                          y2="43"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="68"
                          y1="60"
                          x2="58"
                          y2="42"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="68"
                          y1="60"
                          x2="74"
                          y2="38"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="92"
                          y1="58"
                          x2="86"
                          y2="38"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="92"
                          y1="58"
                          x2="100"
                          y2="36"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="108"
                          y1="58"
                          x2="100"
                          y2="36"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="108"
                          y1="58"
                          x2="114"
                          y2="38"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="132"
                          y1="60"
                          x2="126"
                          y2="38"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="132"
                          y1="60"
                          x2="142"
                          y2="42"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="150"
                          y1="65"
                          x2="142"
                          y2="43"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="150"
                          y1="65"
                          x2="158"
                          y2="45"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="178"
                          y1="70"
                          x2="172"
                          y2="50"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <line
                          x1="178"
                          y1="70"
                          x2="190"
                          y2="55"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l4"
                        />
                        <!-- Level 5 -->
                        <line
                          x1="10"
                          y1="55"
                          x2="4"
                          y2="42"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="10"
                          y1="55"
                          x2="15"
                          y2="40"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="28"
                          y1="50"
                          x2="22"
                          y2="36"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="28"
                          y1="50"
                          x2="34"
                          y2="35"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="42"
                          y1="45"
                          x2="35"
                          y2="30"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="42"
                          y1="45"
                          x2="48"
                          y2="28"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="58"
                          y1="43"
                          x2="52"
                          y2="28"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="58"
                          y1="43"
                          x2="64"
                          y2="26"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="74"
                          y1="38"
                          x2="68"
                          y2="22"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="74"
                          y1="38"
                          x2="80"
                          y2="20"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="86"
                          y1="38"
                          x2="80"
                          y2="22"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="86"
                          y1="38"
                          x2="92"
                          y2="20"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="100"
                          y1="36"
                          x2="95"
                          y2="18"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="100"
                          y1="36"
                          x2="105"
                          y2="18"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="114"
                          y1="38"
                          x2="108"
                          y2="20"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="114"
                          y1="38"
                          x2="120"
                          y2="22"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="126"
                          y1="38"
                          x2="120"
                          y2="20"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="126"
                          y1="38"
                          x2="132"
                          y2="22"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="142"
                          y1="42"
                          x2="136"
                          y2="26"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="142"
                          y1="42"
                          x2="148"
                          y2="28"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="158"
                          y1="45"
                          x2="152"
                          y2="28"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="158"
                          y1="45"
                          x2="165"
                          y2="30"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="172"
                          y1="50"
                          x2="166"
                          y2="35"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="172"
                          y1="50"
                          x2="178"
                          y2="36"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="190"
                          y1="55"
                          x2="185"
                          y2="40"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                        <line
                          x1="190"
                          y1="55"
                          x2="196"
                          y2="42"
                          stroke="#000"
                          stroke-width="1"
                          class="tree-branch tree-l5"
                        />
                      </svg>

                      <!-- Card 2: Heart -->
                      <svg v-else-if="index === 2" viewBox="0 0 200 200" class="w-full h-full">
                        <path
                          d="M100 160 C100 160 29 123 29 78 C29 49 52 34 74 34 C86 34 100 45 100 45 C100 45 114 34 126 34 C148 34 171 49 171 78 C171 123 100 160 100 160 Z"
                          fill="none"
                          stroke="#000"
                          stroke-width="1"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <!-- Card 3: Compass -->
                      <svg
                        v-else-if="index === 3"
                        width="192"
                        height="192"
                        viewBox="0 0 192 192"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style="display: block; margin: auto"
                      >
                        <path d="M96.0049 0.00390625L96.0049 32.0039" stroke="black" />
                        <path d="M96.0049 160L96.0049 192" stroke="black" />
                        <path d="M192.008 95.9961L160.008 95.9961" stroke="black" />
                        <path d="M32.0117 95.9961L0.0117171 95.9961" stroke="black" />
                        <path d="M163.887 28.1172L141.259 50.7446" stroke="black" />
                        <path d="M50.7529 141.25L28.1255 163.877" stroke="black" />
                        <path d="M163.895 163.879L141.267 141.251" stroke="black" />
                        <path d="M50.7607 50.7461L28.1333 28.1187" stroke="black" />
                        <path d="M120.852 3.27344L112.569 34.1831" stroke="black" />
                        <path d="M79.4414 157.816L71.1592 188.726" stroke="black" />
                        <path d="M188.738 120.844L157.829 112.562" stroke="black" />
                        <path d="M34.1943 79.4336L3.28471 71.1514" stroke="black" />
                        <path d="M179.144 48L151.431 64" stroke="black" />
                        <path d="M40.584 127.996L12.8712 143.996" stroke="black" />
                        <path d="M144.014 179.137L128.014 151.424" stroke="black" />
                        <path d="M64.0156 40.5781L48.0156 12.8653" stroke="black" />
                        <path d="M144.005 12.8633L128.005 40.5761" stroke="black" />
                        <path d="M64.0068 151.422L48.0068 179.135" stroke="black" />
                        <path d="M179.148 143.996L151.436 127.996" stroke="black" />
                        <path d="M40.5879 64L12.8751 48" stroke="black" />
                        <path d="M188.734 71.1523L157.825 79.4346" stroke="black" />
                        <path d="M34.1914 112.559L3.28178 120.841" stroke="black" />
                        <path d="M120.86 188.727L112.578 157.817" stroke="black" />
                        <path d="M79.4502 34.1836L71.168 3.27396" stroke="black" />
                        <circle cx="96" cy="96" r="95.5" stroke="black" />
                        <circle cx="96" cy="96" r="63.5" stroke="black" />
                      </svg>
                    </div>
                  </div>

                  <!-- Text at bottom：title (深色) / description (浅色) 两行 -->
                  <div class="mt-auto">
                    <p class="text-lg text-black/80 leading-tight">
                      {{ item.title }}
                    </p>
                    <h3 class="text-lg text-black/40 leading-tight">
                      {{ item.description }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <FeaturesRoles />
    <AppFooter />
  </div>
</template>

<style scoped>
.radley {
  font-family: 'Radley', serif;
}

/* Hero title: word blur animation */
@keyframes wordBlurIn {
  0% {
    opacity: 0;
    filter: blur(12px);
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0);
  }
}

.word-blur {
  display: inline-block;
  margin-right: 0.25em;
  opacity: 0;
  animation: wordBlurIn 0.6s ease-out forwards;
  animation-delay: calc(var(--i) * 0.12s + 0.2s);
}

/* Scroll-triggered elements */
.anim-scroll {
  opacity: 0;
  transform: translateY(32px);
  transition:
    opacity 0.8s ease-out,
    transform 0.8s ease-out;
}

.anim-scroll.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.about-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.about-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}

/* Card 0: Pop-in circles with staggered timing */
[class^='pop-'] {
  opacity: 0;
  animation-fill-mode: backwards;
}
.pop-1 {
  animation: pop 10s ease-in-out 0s infinite;
}
.pop-2 {
  animation: pop 10s ease-in-out 1.2s infinite;
}
.pop-3 {
  animation: pop 10s ease-in-out 2.4s infinite;
}
.pop-4 {
  animation: pop 10s ease-in-out 3.6s infinite;
}
.pop-5 {
  animation: pop 10s ease-in-out 4.8s infinite;
}
.pop-6 {
  animation: pop 10s ease-in-out 6s infinite;
}
.pop-7 {
  animation: pop 10s ease-in-out 7.2s infinite;
}
.pop-8 {
  animation: pop 10s ease-in-out 8.4s infinite;
}
.pop-9 {
  animation: pop 10s ease-in-out 9.6s infinite;
}
.pop-10 {
  animation: pop 10s ease-in-out 0.6s infinite;
}
.pop-11 {
  animation: pop 10s ease-in-out 1.8s infinite;
}
.pop-12 {
  animation: pop 10s ease-in-out 3s infinite;
}

@keyframes pop {
  0%,
  10% {
    opacity: 0;
  }
  15%,
  25% {
    opacity: 1;
  }
  30%,
  100% {
    opacity: 0;
  }
}

/* Card 1: Fractal Tree growing animation */
.tree-branch {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: growBranch 8s ease-out infinite;
}

.tree-trunk {
  animation-delay: 0s;
}
.tree-l1 {
  animation-delay: 0.3s;
}
.tree-l2 {
  animation-delay: 0.6s;
}
.tree-l3 {
  animation-delay: 0.9s;
}
.tree-l4 {
  animation-delay: 1.2s;
}
.tree-l5 {
  animation-delay: 1.5s;
}

@keyframes growBranch {
  0% {
    stroke-dashoffset: 100;
    opacity: 0;
  }
  3% {
    opacity: 1;
  }
  20% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  70% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  85% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
  100% {
    stroke-dashoffset: 100;
    opacity: 0;
  }
}

/* Card 2: Drawing path animation */
.draw-shape {
  stroke-dasharray: 600;
  stroke-dashoffset: 600;
  opacity: 0;
}

.draw-1 {
  animation: drawPath 8s ease-in-out 0s infinite;
}
.draw-2 {
  animation: drawPath 8s ease-in-out 2s infinite;
}
.draw-3 {
  animation: drawPath 8s ease-in-out 4s infinite;
}
.draw-4 {
  animation: drawPath 8s ease-in-out 6s infinite;
}

@keyframes drawPath {
  0% {
    stroke-dashoffset: 600;
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  25% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  35% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  45% {
    stroke-dashoffset: -600;
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    stroke-dashoffset: 600;
    opacity: 0;
  }
}
</style>
